import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Alert from "../../shared/Alert";
import PrimaryButton from "../../shared/buttons/PrimaryButton";
import Loading from "../../shared/Loading";
import {
  ApprovalBadge,
  ApprovalCard,
  ApprovalCopy,
  ApprovalHeading,
  ApprovalSupport,
} from "./PostApprovalPresentation";

export default function PostApprovalPlaidSkipped({
  attempt,
  autoContinue,
  continuation,
  continueToNextRoute,
  creditLimit,
  onContinue,
  onSkipped,
  phase,
  retryEntities,
}) {
  const history = useHistory();
  const skippedAttemptId = useRef(null);
  const continuedTarget = useRef(null);
  const pendingTarget = useRef(null);
  const autoAttemptedTarget = useRef(null);
  const mounted = useRef(false);
  const [continueError, setContinueError] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const isTerminal = phase === "terminal";

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const attemptId = attempt?.get("attempt_id");
    if (!attemptId || skippedAttemptId.current === attemptId) return;

    skippedAttemptId.current = attemptId;
    onSkipped?.(attempt);
  }, [attempt, onSkipped]);

  const continueOnce = useCallback(async () => {
    const target = JSON.stringify(continuation);
    if (
      !isTerminal ||
      !continuation ||
      continuedTarget.current === target ||
      pendingTarget.current === target
    )
      return;

    pendingTarget.current = target;
    setContinueError(false);
    setContinuing(true);
    try {
      await continueToNextRoute(history);
      continuedTarget.current = target;
      onContinue?.(continuation);
    } catch {
      if (mounted.current) setContinueError(true);
    } finally {
      pendingTarget.current = null;
      if (mounted.current) setContinuing(false);
    }
  }, [continuation, continueToNextRoute, history, isTerminal, onContinue]);

  useEffect(() => {
    const target = JSON.stringify(continuation);
    if (
      !autoContinue ||
      !continuation ||
      autoAttemptedTarget.current === target
    )
      return;

    autoAttemptedTarget.current = target;
    continueOnce();
  }, [autoContinue, continuation, continueOnce]);

  const retry = () => {
    Promise.resolve(retryEntities()).catch(() => undefined);
  };

  return (
    <ApprovalCard role="status">
      <ApprovalBadge />
      <ApprovalHeading creditLimit={creditLimit} />
      <ApprovalCopy />
      {phase === "entity_refresh_error" && (
        <div className="mb-3">
          <Alert
            containerClass="bg-red-50 p-4"
            icon={["far", "circle-xmark"]}
            alertText="We couldn't update your application. Your current approval is unchanged."
          />
          <button
            className="mt-2 text-sm text-primary-blue underline"
            onClick={retry}
            type="button"
          >
            Try again
          </button>
        </div>
      )}
      {continueError && (
        <div className="mb-3" role="alert">
          <Alert
            containerClass="bg-red-50 p-4"
            icon={["far", "circle-xmark"]}
            alertText="We couldn't continue. Please try again."
          />
          <button
            className="mt-2 text-sm text-primary-blue underline"
            onClick={continueOnce}
            type="button"
          >
            Try again
          </button>
        </div>
      )}
      {!isTerminal && <Loading className="w-8 h-8" containerClass="my-4" />}
      <PrimaryButton
        buttonColor="bg-primary-blue"
        buttonText="Continue"
        disabled={!isTerminal || !continuation || continuing}
        hoverClass="hover:bg-primary-blue-hover"
        onClick={(event) => {
          event.preventDefault();
          continueOnce();
        }}
      />
      <ApprovalSupport />
    </ApprovalCard>
  );
}
